import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { securityLogger } from '@/utils/securityLogger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Defer logging to avoid Supabase calls inside the auth callback (prevents iframe deadlocks)
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            securityLogger.logEvent({
              event_type: 'admin_action',
              user_id: session.user.id,
              details: { action: 'user_signed_in', event },
              severity: 'low'
            });
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setTimeout(() => {
            securityLogger.logEvent({
              event_type: 'admin_action',
              details: { action: 'user_signed_out', event },
              severity: 'low'
            });
          }, 0);
        } else if (event === 'TOKEN_REFRESHED') {
          setTimeout(() => {
            securityLogger.logEvent({
              event_type: 'admin_action',
              user_id: session?.user?.id,
              details: { action: 'token_refreshed', event },
              severity: 'low'
            });
          }, 0);
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading }}>
      {children}
    </AuthContext.Provider>
  );
};